import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActiveTimerIndicator = () => {
  const navigate = useNavigate();
  const [timerData, setTimerData] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Fetch timer attivo
  const fetchActiveTimer = useCallback(async () => {
    try {
      // Prima controlla localStorage (più veloce)
      const localTimer = localStorage.getItem('activeTimer');
      if (localTimer) {
        const parsed = JSON.parse(localTimer);
        if (parsed.isRunning && parsed.startTime) {
          setTimerData({
            codice_progetto: parsed.projectCode,
            nome_progetto: parsed.projectName,
            start_time: parsed.startTime,
            is_running: true
          });
          setElapsedTime(Math.floor((Date.now() - parsed.startTime) / 1000));
          return;
        }
      }

      // Fallback al backend
      const token = localStorage.getItem('token');
      if (!token) {
        setTimerData(null);
        return;
      }

      const response = await axios.get(`${API}/time-entries/timer-attivo`, getAuthHeaders());
      if (response.data && response.data.is_running) {
        const startTime = new Date(response.data.start_time).getTime();
        setTimerData({
          codice_progetto: response.data.codice_progetto,
          nome_progetto: response.data.nome_progetto,
          start_time: startTime,
          is_running: true
        });
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
        
        // Sync to localStorage
        localStorage.setItem('activeTimer', JSON.stringify({
          isRunning: true,
          startTime: startTime,
          projectCode: response.data.codice_progetto,
          projectName: response.data.nome_progetto
        }));
      } else {
        setTimerData(null);
        localStorage.removeItem('activeTimer');
      }
    } catch (error) {
      // Se errore, controlla solo localStorage
      const localTimer = localStorage.getItem('activeTimer');
      if (localTimer) {
        const parsed = JSON.parse(localTimer);
        if (parsed.isRunning && parsed.startTime) {
          setTimerData({
            codice_progetto: parsed.projectCode,
            nome_progetto: parsed.projectName,
            start_time: parsed.startTime,
            is_running: true
          });
          setElapsedTime(Math.floor((Date.now() - parsed.startTime) / 1000));
          return;
        }
      }
      setTimerData(null);
    }
  }, []);

  // Initial fetch e polling
  useEffect(() => {
    fetchActiveTimer();
    
    // Polling ogni 3 secondi
    const pollInterval = setInterval(fetchActiveTimer, 3000);
    
    // Listen for storage changes
    const handleStorageChange = () => {
      fetchActiveTimer();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [fetchActiveTimer]);

  // Aggiorna contatore ogni secondo
  useEffect(() => {
    if (!timerData || !timerData.is_running) return;
    
    const tickInterval = setInterval(() => {
      if (timerData.start_time) {
        setElapsedTime(Math.floor((Date.now() - timerData.start_time) / 1000));
      }
    }, 1000);
    
    return () => clearInterval(tickInterval);
  }, [timerData]);

  // Formatta tempo HH:MM:SS
  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Non mostrare se non c'è timer attivo
  if (!timerData || !timerData.is_running) return null;

  return (
    <div 
      onClick={() => navigate('/controllo-gestione')}
      className="fixed top-4 right-4 z-[9999] cursor-pointer"
      title="Clicca per aprire Time Tracking"
      data-testid="active-timer-indicator"
    >
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg shadow-xl flex items-center gap-3 hover:from-green-600 hover:to-emerald-700 transition-all border-2 border-white/20">
        {/* Icona animata */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Clock className="w-5 h-5 animate-pulse" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
          </div>
          <span className="font-mono font-bold text-lg tracking-wider">
            {formatTime(elapsedTime)}
          </span>
        </div>
        
        {/* Codice progetto */}
        <div className="border-l border-white/30 pl-3">
          <p className="text-[10px] uppercase opacity-70 tracking-wider">Codice Progetto</p>
          <p className="font-bold text-sm truncate max-w-[180px]">
            {timerData.codice_progetto || timerData.nome_progetto || 'Timer Attivo'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActiveTimerIndicator;
